// src/components/auth/AuthModal.tsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  IconButton,
  Typography,
  Alert,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { LoginForm } from "./LoginForm";
import { SignUpForm } from "./SignUpForm";
import { PasswordReset } from "./PasswordReset";
import { GoogleConsentStep } from "./GoogleConsentStep";
import { GoogleLinkStep } from "./GoogleLinkStep";
import { useAuthStore } from "../../stores/authStore";
import { useT } from '../../i18n/useT';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: "login" | "signup" | "reset";
}

export const AuthModal: React.FC<AuthModalProps> = ({
  open,
  onClose,
  initialTab = "login",
}) => {
  const t = useT();
  const [activeTab, setActiveTab] = useState(initialTab);
  const {
    authMode,
    error,
    clearError,
    pendingGoogleConsent,
    cancelGoogleConsent,
    pendingGoogleLink,
    cancelGoogleLink,
  } = useAuthStore();

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue as "login" | "signup" | "reset");
    clearError();
  };

  const handleClose = () => {
    clearError();
    onClose();
  };

  // If a Google consent flow is pending, treat dialog close as
  // explicit cancel — we need to clean up the half-created Firebase
  // user. The user pressing Esc, clicking the backdrop, or clicking
  // the X all funnel through this.
  //
  // The link flow is different: nothing was actually written yet,
  // we just have a captured OAuth credential in memory. We drop it
  // on close so the user lands on a clean tabbed view next time.
  const handleCloseWithConsentCheck = async () => {
    if (pendingGoogleConsent) {
      await cancelGoogleConsent();
    }
    if (pendingGoogleLink) {
      cancelGoogleLink();
    }
    handleClose();
  };

  const isUpgrade = authMode === "anonymous";
  const isPendingConsent = !!pendingGoogleConsent;
  const isPendingLink = !!pendingGoogleLink;
  // Hide the regular tabs/forms whenever a special flow is active.
  const isSpecialFlow = isPendingConsent || isPendingLink;

  // TEMP DIAGNOSTIC.
  console.log("[AuthModal] render", {
    open,
    isPendingConsent,
    isPendingLink,
    isSpecialFlow,
    activeTab,
  });

  return (
    <Dialog
      open={open}
      onClose={handleCloseWithConsentCheck}
      maxWidth="sm"
      fullWidth
      // While we're in the Google-consent step, prevent accidental
      // dismissals via backdrop click — explicit Cancel button
      // handles the cleanup. Esc still works (handleClose path).
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isPendingConsent
              ? t("Almost there")
              : isPendingLink
              ? "Connect your account"
              : isUpgrade
              ? t("Save Your Conversations")
              : "Account Access"}
          </Typography>
          {!isSpecialFlow && (
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Consent step — new Google user, needs GDPR consent. */}
        {isPendingConsent && (
          <GoogleConsentStep onComplete={handleClose} />
        )}

        {/* Link step — Google email collides with an existing
            email/password account; ask for the password and link. */}
        {!isPendingConsent && isPendingLink && (
          <GoogleLinkStep onComplete={handleClose} />
        )}

        {/* Regular tabbed view */}
        {!isSpecialFlow && (
          <>
            {isUpgrade && (
              <Alert severity="info" sx={{ mb: 3 }}>
                
                {t("Create an account to save your conversations and sync across devices. Your current conversation history will be preserved.")}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {activeTab !== "reset" && (
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{ mb: 3 }}
                centered
              >
                <Tab label={t("Sign In")} value="login" />
                <Tab label={t("Create Account")} value="signup" />
              </Tabs>
            )}

            <Box>
              {activeTab === "login" && (
                <LoginForm
                  onSuccess={handleClose}
                  onForgotPassword={() => setActiveTab("reset")}
                  onSwitchToSignup={() => setActiveTab("signup")}
                  isUpgrade={isUpgrade}
                />
              )}

              {activeTab === "signup" && (
                <SignUpForm
                  onSuccess={handleClose}
                  onSwitchToLogin={() => setActiveTab("login")}
                  isUpgrade={isUpgrade}
                />
              )}

              {activeTab === "reset" && (
                <PasswordReset onBack={() => setActiveTab("login")} />
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
